import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { BookCheck, ChevronDown } from 'lucide-react'
import { Button } from '@quebon/ui/components/button'
import clsx from 'clsx'
import type {
  ManualGradeCredit,
  WorkbookGradeItem,
  WorkbookGradeView,
  WorkbookMode,
} from '@/types/coaching'
import { PartialCreditGradeToggle } from './component-details/parts/PartialCreditGradeToggle'
import { MarkAllCorrectButton } from './MarkAllCorrectButton'
import { useGradeWorkbook, useWorkbookGrades } from '@/hooks/coaching/useWorkbookGrades'

interface SectionProps {
  learnerId: number
  lessonId: number
  actions?: ReactNode
  canEdit?: boolean
  workbookMode?: WorkbookMode
  onWorkbookModeChange?: (mode: WorkbookMode) => void
  bookTitle?: string | null
  startPage?: number | null
}

/**
 * 워크북(복습용 오프라인 인쇄물, CORE 트랙만) 강사 수기채점 섹션 (G7, 결정 시나리오 A 축5).
 *
 * 워크북은 디지털 컴포넌트가 아니라 **레슨 단위 오프라인** 채점이므로 컴포넌트별 채점 UI 와 별개로
 * 레슨 단위 토글 패널(DailyWrongAnswerPanel 패턴 미러)로 제공한다. 채점은 종이를 보고 강사가 수기 입력:
 *  - 문제(PROBLEM) 5문제: 정답(1) / 부분정답(0.5) / 오답(0).
 * CORE D/E 워크북에 물리적으로 포함된 생각열기·포인트·블록퀴즈는 학습진행설정 v2 warmup/video(pausequiz) 흐름으로 다룬다.
 * 저장은 입력된 문항만 batch 멱등 upsert(미입력 문항은 보내지 않음).
 */
export function WorkbookGradingSection({
  learnerId,
  lessonId,
  actions,
  canEdit = true,
  workbookMode = 'IMMEDIATE',
  onWorkbookModeChange,
  bookTitle = null,
  startPage = null,
}: SectionProps) {
  const { t } = useTranslation('coaching')
  const [open, setOpen] = useState(false)

  return (
    <div className="border-b border-gray-100 pb-3">
      <div className="flex flex-wrap items-center justify-between gap-2" data-testid="workbook-action-row">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={clsx(
            'inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-xs font-medium',
            open
              ? 'border-lc-mint-300 bg-lc-mint-100 text-lc-mint-dark'
              : 'border-lc-mint-200 bg-lc-mint-100/60 text-lc-mint-dark hover:bg-lc-mint-100',
          )}
        >
          <BookCheck size={14} /> {t('workbook.toggle')}
          <ChevronDown size={14} className={clsx('transition-transform', open && 'rotate-180')} />
        </button>
        {actions && <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>}
      </div>
      {open && (
        <div className="mt-3">
          <WorkbookGradingPanel
            learnerId={learnerId}
            lessonId={lessonId}
            canEdit={canEdit}
            workbookMode={workbookMode}
            onWorkbookModeChange={onWorkbookModeChange}
            bookTitle={bookTitle}
            startPage={startPage}
          />
        </div>
      )}
    </div>
  )
}

type Draft = { partialCredit: ManualGradeCredit | null }

const PROBLEM_COUNT = 5
const keyOf = (no: number) => `PROBLEM:${no}`

function buildInitialDrafts(grades: WorkbookGradeView[]): Record<string, Draft> {
  const map: Record<string, Draft> = {}
  for (const g of grades) {
    if (g.itemKind === 'PROBLEM') {
      map[keyOf(g.itemNo)] = {
        partialCredit: g.partialCredit
          ?? (g.isCorrect == null ? null : g.isCorrect ? 1 : 0),
      }
    }
  }
  return map
}

interface PanelProps {
  learnerId: number
  lessonId: number
  canEdit?: boolean
  workbookMode?: WorkbookMode
  onWorkbookModeChange?: (mode: WorkbookMode) => void
  bookTitle?: string | null
  startPage?: number | null
}

const WORKBOOK_MODES: WorkbookMode[] = ['IMMEDIATE', 'ASSIGNMENT', 'NEXT_CLASS']

export function WorkbookGradingPanel({
  learnerId,
  lessonId,
  canEdit = true,
  workbookMode = 'IMMEDIATE',
  onWorkbookModeChange,
  bookTitle = null,
  startPage = null,
}: PanelProps) {
  const { t } = useTranslation('coaching')
  const { data: grades, isLoading, isError, refetch } = useWorkbookGrades(learnerId, lessonId)
  const gradeMutation = useGradeWorkbook(learnerId, lessonId)
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})

  // 서버 채점이 로드되면 draft 초기화(재채점 prefill).
  useEffect(() => {
    if (grades) setDrafts(buildInitialDrafts(grades))
  }, [grades])

  function setGrade(no: number, partialCredit: ManualGradeCredit) {
    if (!canEdit || gradeMutation.isPending || isLoading || isError) return
    setDrafts((cur) => {
      const k = keyOf(no)
      const nextValue = cur[k]?.partialCredit === partialCredit ? null : partialCredit
      return { ...cur, [k]: { partialCredit: nextValue } }
    })
  }

  function markAllCorrect() {
    if (!canEdit || gradeMutation.isPending || isLoading || isError) return
    setDrafts((current) => {
      const next = { ...current }
      for (let no = 1; no <= PROBLEM_COUNT; no++) next[keyOf(no)] = { partialCredit: 1 }
      return next
    })
  }

  // 입력된 5문제 채점만 전송(미입력=null 은 스킵 → 생성 안 함).
  const items = useMemo<WorkbookGradeItem[]>(() => {
    const out: WorkbookGradeItem[] = []
    for (let no = 1; no <= PROBLEM_COUNT; no++) {
      const d = drafts[keyOf(no)]
      if (!d || d.partialCredit === null) continue
      out.push({
        itemKind: 'PROBLEM',
        itemNo: no,
        isCorrect: d.partialCredit === 1,
        partialCredit: d.partialCredit,
        submittedResponse: undefined,
      })
    }
    return out
  }, [drafts])

  function handleSave() {
    if (!canEdit || gradeMutation.isPending || isLoading || isError || items.length === 0) return
    gradeMutation.mutate({ lessonId, items })
  }

  if (isLoading) {
    return <p className="mt-3 text-xs text-gray-500" role="status">{t('workbook.loading')}</p>
  }

  if (isError) {
    return <div role="alert" className="flex flex-col gap-3 p-4">
      <p>{t('library.loadError')}</p>
      <Button type="button" variant="outline" className="min-h-11" onClick={() => void refetch()}>{t('roster.retry')}</Button>
    </div>
  }

  return (
    <div
      data-testid="workbook-grading-shell"
      className="space-y-4 rounded-xl border border-emerald-200 bg-[linear-gradient(145deg,var(--color-emerald-50),white_42%,var(--color-lc-mint-100))] p-4 shadow-sm"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-emerald-600 text-white shadow-[0_3px_0_0_var(--color-emerald-700)]">
            <BookCheck size={18} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <h3 className="truncate text-sm font-bold text-gray-950">{t('operationStep.workbook')}</h3>
              {bookTitle ? (
                <span
                  className="shrink-0 rounded bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800"
                  data-testid="workbook-start-page"
                >
                  {startPage != null
                    ? t('lessonMaterial.bookWithPage', { bookName: bookTitle, page: startPage })
                    : bookTitle}
                </span>
              ) : null}
            </div>
            <p className="text-xs leading-relaxed text-gray-600">{t('workbook.hint')}</p>
          </div>
        </div>
        {!canEdit && (
          <span className="shrink-0 rounded bg-gray-200 px-2 py-1 text-[11px] font-semibold text-gray-600">
            {t('operationStep.readOnly')}
          </span>
        )}
      </div>

      <fieldset className="rounded-xl border border-emerald-100 bg-white/95 p-3.5 shadow-sm" disabled={!canEdit || gradeMutation.isPending}>
        <legend className="mb-2 text-xs font-bold text-gray-700">{t('workbook.mode.title')}</legend>
        <div className="grid gap-2 sm:grid-cols-3">
          {WORKBOOK_MODES.map((mode) => (
            <label
              key={mode}
              className={clsx(
                'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium',
                workbookMode === mode
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                  : 'border-gray-200 bg-white text-gray-700',
                !canEdit && 'cursor-not-allowed opacity-60',
              )}
            >
              <input
                type="radio"
                name={`workbook-mode-${lessonId}`}
                value={mode}
                checked={workbookMode === mode}
                onChange={() => onWorkbookModeChange?.(mode)}
              />
              {t(`workbook.mode.${mode}`)}
            </label>
          ))}
        </div>
      </fieldset>

      <section className="rounded-xl border border-emerald-100 bg-white/95 p-3.5 shadow-sm">
        {/* 문제 5문제 — 정답 / 부분정답 / 오답 */}
        <fieldset>
          <legend className="mb-2 flex w-full items-center justify-between gap-2">
            <span className="text-xs font-bold text-gray-700">{t('workbook.problemTitle')}</span>
            <MarkAllCorrectButton onClick={markAllCorrect} disabled={!canEdit || gradeMutation.isPending} />
          </legend>
          <div className="flex flex-col gap-2">
            {Array.from({ length: PROBLEM_COUNT }, (_, i) => i + 1).map((no) => (
              <ItemRow
                key={`PROBLEM-${no}`}
                label={t('workbook.itemNo', { no })}
                value={drafts[keyOf(no)]?.partialCredit ?? null}
                onChange={(grade) => setGrade(no, grade)}
                disabled={!canEdit || gradeMutation.isPending}
              />
            ))}
          </div>
        </fieldset>

        <div className="mt-3 flex items-center justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={!canEdit || gradeMutation.isPending || items.length === 0}
            className="rounded-md bg-lc-mint-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-lc-mint-dark disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {gradeMutation.isPending ? t('workbook.saving') : t('workbook.save')}
          </button>
        </div>
      </section>
    </div>
  )
}

interface ItemRowProps {
  label: string
  value: ManualGradeCredit | null
  onChange: (grade: ManualGradeCredit) => void
  disabled?: boolean
}

function ItemRow({ label, value, onChange, disabled }: ItemRowProps) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-16 shrink-0 text-lg font-bold tabular-nums text-gray-900">{label}</span>
      <PartialCreditGradeToggle value={value} onChange={onChange} disabled={disabled} />
    </div>
  )
}
